import React from 'react';
import { FD140, FD141 } from '../../../services/d204Api';
import { fmt } from '../helpers';

interface Tab3Props {
  formData: Partial<FD140>;
  details: Partial<FD141>[];
  selectedRow: number;
  isEditing: boolean;
  setSelectedRow: (idx: number) => void;
  getDetailField: (key: keyof FD141) => FD141[keyof FD141];
  setDetailField: (key: keyof FD141, value: FD141[keyof FD141]) => void;
  addDetailRow: () => void;
  deleteDetailRow: () => void;
}

const Tab3Detail: React.FC<Tab3Props> = React.memo(({
  formData, details, selectedRow, isEditing, setSelectedRow,
  getDetailField, setDetailField,
  addDetailRow, deleteDetailRow,
}) => {
  return (
    <div className="form-section odd">
      <span className="section-tag">入庫明細</span>

      <div style={{ display: 'flex', gap: 16, marginBottom: 8, alignItems: 'center' }}>
        <label style={{ fontSize: 13 }}>總項數</label>
        <input type="text" readOnly style={{ width: 60 }} value={formData.fd140_35 ?? ''} data-contract-field="fd140_35" />
      </div>

      <div className="table-wrapper">
        <div className="table-toolbar">
          <div className="left">
            <span style={{ fontSize: 13, color: '#595959' }}>明細 ({details.length} 項)</span>
          </div>
          <div className="right">
            {isEditing && (
              <>
                <button className="btn btn-sm" onClick={addDetailRow}>新增明細</button>
                <button className="btn btn-sm btn-danger" onClick={deleteDetailRow}
                  disabled={details.length === 0}>刪除明細</button>
              </>
            )}
          </div>
        </div>

        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 40 }} data-contract-field="fd141_02">#</th>
                <th data-contract-field="fd141_11">等級</th>
                <th data-contract-field="fd141_111">等級名稱</th>
                <th data-contract-field="fd141_12">顏色</th>
                <th data-contract-field="fd141_20">顏色英文名</th>
                <th data-contract-field="fd141_13">單位</th>
                <th className="num" data-contract-field="fd141_21">託SEQ</th>
                <th className="num" data-contract-field="fd141_15">入庫數量</th>
                <th className="num" data-contract-field="fd141_19">換算率</th>
                <th className="num" data-contract-field="fd141_14">工繳單價</th>
                <th className="num" data-contract-field="fd141_141">一缸工繳</th>
                <th className="num" data-contract-field="fd141_16">工繳金額</th>
              </tr>
            </thead>
            <tbody>
              {details.length === 0 ? (
                <tr><td colSpan={12} style={{ textAlign: 'center', color: '#999' }}>尚無明細</td></tr>
              ) : (
                details.map((d, i) => (
                  <tr key={i}
                    className={selectedRow === i ? 'selected' : ''}
                    onClick={() => setSelectedRow(i)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>{d.fd141_02 || i + 1}</td>
                    <td>{d.fd141_11 || ''}</td>
                    <td>{d.fd141_111 || ''}</td>
                    <td>{d.fd141_12 || ''}</td>
                    <td>{d.fd141_20 || ''}</td>
                    <td>{d.fd141_13 || ''}</td>
                    <td className="num">{d.fd141_21 ?? ''}</td>
                    <td className="num">{fmt(d.fd141_15 as number)}</td>
                    <td className="num">{fmt(d.fd141_19 as number)}</td>
                    <td className="num">{fmt(d.fd141_14 as number)}</td>
                    <td className="num">{fmt(d.fd141_141 as number)}</td>
                    <td className="num">{fmt(d.fd141_16 as number)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail panel below grid */}
      {details.length > 0 && (
        <div className="detail-panel">
          <label>等級代號</label>
          <input type="text" style={{ maxWidth: 80 }}
            disabled={!isEditing}
            value={getDetailField('fd141_11') as string}
            onChange={e => setDetailField('fd141_11', e.target.value)} data-contract-field="fd141_11"
          />

          <label>等級名稱</label>
          <input type="text" style={{ maxWidth: 200 }}
            disabled={!isEditing}
            value={getDetailField('fd141_111') as string}
            onChange={e => setDetailField('fd141_111', e.target.value)} data-contract-field="fd141_111"
          />

          <label>顏色代號</label>
          <input type="text" style={{ maxWidth: 80 }}
            disabled={!isEditing}
            value={getDetailField('fd141_12') as string}
            onChange={e => setDetailField('fd141_12', e.target.value)} data-contract-field="fd141_12"
          />

          <label>顏色英文名</label>
          <input type="text" style={{ maxWidth: 200 }}
            disabled={!isEditing}
            value={getDetailField('fd141_20') as string}
            onChange={e => setDetailField('fd141_20', e.target.value)} data-contract-field="fd141_20"
          />

          <label>單位</label>
          <input type="text" style={{ maxWidth: 60 }}
            disabled={!isEditing}
            value={getDetailField('fd141_13') as string}
            onChange={e => setDetailField('fd141_13', e.target.value)} data-contract-field="fd141_13"
          />

          <label>入庫數量</label>
          <input type="number" style={{ maxWidth: 100 }} step="0.01"
            disabled={!isEditing}
            value={getDetailField('fd141_15') as number || ''}
            onChange={e => setDetailField('fd141_15', parseFloat(e.target.value) || 0)} data-contract-field="fd141_15"
          />

          <label>單位換算率</label>
          <input type="number" style={{ maxWidth: 100 }} step="0.001"
            disabled={!isEditing}
            value={getDetailField('fd141_19') as number || ''}
            onChange={e => setDetailField('fd141_19', parseFloat(e.target.value) || 0)} data-contract-field="fd141_19"
          />

          <label>工繳單價</label>
          <input type="number" style={{ maxWidth: 120 }} step="0.001"
            disabled={!isEditing}
            value={getDetailField('fd141_14') as number || ''}
            onChange={e => setDetailField('fd141_14', parseFloat(e.target.value) || 0)} data-contract-field="fd141_14"
          />

          <label>工繳金額</label>
          <input type="text" readOnly style={{ maxWidth: 120, textAlign: 'right' }}
            value={fmt(getDetailField('fd141_16') as number)} data-contract-field="fd141_16"
          />

          <label>一缸工繳</label>
          <input type="number" style={{ maxWidth: 120 }} step="0.01"
            disabled={!isEditing}
            value={getDetailField('fd141_141') as number || ''}
            onChange={e => setDetailField('fd141_141', parseFloat(e.target.value) || 0)} data-contract-field="fd141_141"
          />

          <label>S/C-NO</label>
          <input type="text" style={{ maxWidth: 120 }}
            disabled={!isEditing}
            value={getDetailField('fd141_28') as string}
            onChange={e => setDetailField('fd141_28', e.target.value)} data-contract-field="fd141_28"
          />

          <label>S/C項次</label>
          <input type="text" readOnly style={{ maxWidth: 60 }}
            value={getDetailField('fd141_22') as number ?? ''} data-contract-field="fd141_22"
          />

          <label>CBNO.</label>
          <input type="text" readOnly style={{ maxWidth: 120 }}
            value={getDetailField('fd141_12a') as string} data-contract-field="fd141_12a"
          />
        </div>
      )}
    </div>
  );
});

Tab3Detail.displayName = 'Tab3Detail';
export default Tab3Detail;
